import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Smartphone, Download, Zap, Image, Sparkles, TrendingUp } from 'lucide-react';

/**
 * Mobile App Referral Landing Page
 * 
 * URL: https://metapic.app/app?ref=AUTOKI92
 * 
 * Features:
 * - Track referral click to backend
 * - Save referral code to localStorage for mobile app to read
 * - Redirect to Google Play Store
 * - Show loading state during tracking
 */
export default function MobileAppReferralPage() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const [isTracking, setIsTracking] = useState(true);
  const [trackingError, setTrackingError] = useState(false);

  useEffect(() => {
    // If no referral code, redirect to regular mobile app page
    if (!referralCode) {
      window.location.href = '/mobile-app';
      return;
    }

    // Track referral click then redirect immediately
    trackReferralClick();
    
    // Save referral code to localStorage for mobile app to read after install
    try {
      localStorage.setItem('metapic_mobile_referral_code', referralCode);
      localStorage.setItem('metapic_mobile_referral_timestamp', new Date().toISOString());
    } catch (error) {
      console.error('Failed to save referral code to localStorage:', error);
    }
  }, [referralCode]);

  const trackReferralClick = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      
      // Build query parameters
      const params = new URLSearchParams({
        ref: referralCode!,
        landing: 'mobile_app'
      });
      
      const response = await fetch(`${apiUrl}/api/v1/referral/click?${params.toString()}`, {
        method: 'GET',
        headers: {
          'User-Agent': navigator.userAgent,
          'Referer': document.referrer || 'direct'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to track referral');
      }

      const data = await response.json();
      console.log('✅ Referral click tracked:', data);
      
    } catch (error) {
      console.error('❌ Failed to track referral click:', error);
      setTrackingError(true);
    } finally {
      setIsTracking(false);
      // Redirect immediately after tracking attempt (success or fail)
      redirectToGooglePlay();
    }
  };

  const redirectToGooglePlay = () => {
    window.location.href = 'https://play.google.com/store/apps/details?id=com.metapic.app';
  };

  return (
    <>
      <Helmet>
        <title>Download MetaPic - Referred by {referralCode}</title>
        <meta name="description" content="Join MetaPic and start creating professional stock photography content with AI-powered tools." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center space-y-6">
            {/* App Icon */}
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-12 h-12 text-white" />
            </div>

            {/* Title */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome to MetaPic!
              </h1>
              <p className="text-gray-600">
                You've been referred by <span className="font-semibold text-indigo-600">{referralCode}</span>
              </p>
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 space-y-3 text-left">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">AI Metadata Generator</p>
                  <p className="text-sm text-gray-600">Auto-generate keywords & titles</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Image className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Image Upscaler</p>
                  <p className="text-sm text-gray-600">Enhance image quality to 4K</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Background Remover</p>
                  <p className="text-sm text-gray-600">Remove backgrounds instantly</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">FTP Upload</p>
                  <p className="text-sm text-gray-600">Direct upload to stock sites</p>
                </div>
              </div>
            </div>

            {/* Status */}
            {isTracking ? (
              <div className="flex items-center justify-center gap-2 text-gray-600">
                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Preparing download...</span>
              </div>
            ) : trackingError ? (
              <div className="text-yellow-600 text-sm">
                ⚠️ Tracking failed, but you can still download the app
              </div>
            ) : (
              <div className="text-green-600 text-sm flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Ready to download
              </div>
            )}

            {/* Download Button */}
            <button
              onClick={redirectToGooglePlay}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Download from Google Play
            </button>

            {/* Google Play Badge */}
            <div className="pt-2">
              <img 
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-14 mx-auto cursor-pointer opacity-90 hover:opacity-100 transition-opacity"
                onClick={redirectToGooglePlay}
              />
            </div>

            {/* Info */}
            <p className="text-xs text-gray-500">
              Your referral code will be automatically applied when you sign up in the app
            </p>
          </div>

          {/* Skip Button */}
          <button
            onClick={redirectToGooglePlay}
            className="w-full mt-4 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
          >
            Skip and go to Google Play →
          </button>
        </div>
      </div>
    </>
  );
}
